/**
 * Seed data + in-memory fixtures for the legacy/migrated `/settings` section
 * (FRO-363). Shapes are copied verbatim from the captured wire truth in
 * `openspec/changes/fro-363-migrate-settings-section/research/04-browser-walkthrough.md`
 * §5 — the single ground truth both the legacy tech-ui view and the migrated
 * connect feature must accept:
 *
 * - the notification list is a BARE JSON array (not `{ data: [...] }`),
 * - create returns `{ "success": true }`,
 * - delete returns a `200` with an empty body,
 * - the enums payload is the exact object observed on the wire.
 *
 * The notification list is one mutable in-memory array so create/edit/delete
 * mutate the same instance a subsequent list re-fetch reads back (both remotes
 * share the router's single msw worker — adr/0003). A full page load resets the
 * module, so Playwright's per-test isolation gives every test the seed again.
 */

/** One row of `GET /public/admin/notification-settings/{operatorId}`. */
interface NotificationSetting {
  id: number;
  operatorId: number;
  companyId: number;
  triggerTypeId: number;
  triggerType: string;
  notificationTypeId: number;
  notificationType: string;
  address: string;
  templateId: number;
  templateName: string;
  // `1` -> "Yes", `0`/`null` -> "No"; an absent value renders a BLANK Active
  // cell in legacy (research/04 §2 edge case), so row 203 omits it.
  active?: number | null;
  subject: string;
  createdAt: string;
  updatedAt: string;
}

const NOTIFICATION_SEED: NotificationSetting[] = [
  {
    id: 201,
    operatorId: 1,
    companyId: 1,
    triggerTypeId: 1,
    triggerType: "email",
    notificationTypeId: 1,
    notificationType: "chargeBoxActivationResidential",
    address: "alice@example.com",
    templateId: 1,
    templateName: "Add ChargeBox",
    active: 1,
    subject: "Residential activation",
    createdAt: "2026-01-05T09:00:00.000Z",
    updatedAt: "2026-01-05T09:00:00.000Z",
  },
  {
    id: 202,
    operatorId: 1,
    companyId: 1,
    triggerTypeId: 1,
    triggerType: "email",
    notificationTypeId: 5,
    notificationType: "exportToCsv",
    address: "bob@example.com",
    templateId: 2,
    templateName: "Export to CSV",
    active: 1,
    subject: "CSV export",
    createdAt: "2026-01-06T09:00:00.000Z",
    updatedAt: "2026-01-06T09:00:00.000Z",
  },
  {
    // The empty-Active edge case (research/04 §2): `active` is absent, so the
    // Active cell renders blank rather than Yes/No.
    id: 203,
    operatorId: 1,
    companyId: 1,
    triggerTypeId: 1,
    triggerType: "email",
    notificationTypeId: 3,
    notificationType: "generateVouchers",
    address: "carol@example.com",
    templateId: 3,
    templateName: "Generate Vouchers",
    active: undefined,
    subject: "Vouchers",
    createdAt: "2026-01-07T09:00:00.000Z",
    updatedAt: "2026-01-07T09:00:00.000Z",
  },
  {
    id: 204,
    operatorId: 1,
    companyId: 1,
    triggerTypeId: 1,
    triggerType: "email",
    notificationTypeId: 4,
    notificationType: "evseOffline",
    address: "dave@example.com",
    templateId: 4,
    templateName: "Chargebox Error",
    active: 1,
    subject: "Offline alert",
    createdAt: "2026-01-08T09:00:00.000Z",
    updatedAt: "2026-01-08T09:00:00.000Z",
  },
  {
    // active: 0 -> "No" (legacy MAP_NUMBER_TO_CHOICE[0]). Real dev data carries
    // inactive rows; the earlier seed had none, which hid a migrated regression
    // (0 was rendered blank instead of "No").
    id: 205,
    operatorId: 1,
    companyId: 1,
    triggerTypeId: 1,
    triggerType: "email",
    notificationTypeId: 2,
    notificationType: "chargeBoxActivationOther",
    address: "erin@example.com",
    templateId: 1,
    templateName: "Add ChargeBox",
    active: 0,
    subject: "Inactive alert",
    createdAt: "2026-01-09T09:00:00.000Z",
    updatedAt: "2026-01-09T09:00:00.000Z",
  },
  {
    // active: 2 -> blank via legacy map miss. Dev row id 184 really returns
    // active: 2; the strict `0|1` schema rejected it and blanked the whole
    // list. Kept here so both projects prove the non-canonical value renders
    // a blank cell (and never fails the parse).
    id: 206,
    operatorId: 1,
    companyId: 1,
    triggerTypeId: 1,
    triggerType: "email",
    notificationTypeId: 5,
    notificationType: "exportToCsv",
    address: "frank@example.com",
    templateId: 2,
    templateName: "Export to CSV",
    active: 2,
    subject: "Non-canonical active",
    createdAt: "2026-01-10T09:00:00.000Z",
    updatedAt: "2026-01-10T09:00:00.000Z",
  },
];

/** The live, mutable fixture the handlers read and write. */
let notifications: NotificationSetting[] = NOTIFICATION_SEED.map((row) => ({
  ...row,
}));

/**
 * Exact `GET /public/enums/?notificationType=1&notificationTriggerType=1`
 * response (research/04 §5). Drives the create/edit selects.
 */
const NOTIFICATION_ENUMS_RESPONSE = {
  notificationTriggerType: [{ id: 1, triggerType: "email" }],
  notificationType: [
    { id: 1, notificationType: "chargeBoxActivationResidential" },
    { id: 2, notificationType: "chargeBoxActivationOther" },
    { id: 3, notificationType: "generateVouchers" },
    { id: 4, notificationType: "evseOffline" },
    { id: 5, notificationType: "exportToCsv" },
  ],
} as const;

function triggerTypeLabel(id: number): string {
  return (
    NOTIFICATION_ENUMS_RESPONSE.notificationTriggerType.find((t) => t.id === id)
      ?.triggerType ?? "email"
  );
}

function notificationTypeLabel(id: number): string {
  return (
    NOTIFICATION_ENUMS_RESPONSE.notificationType.find((t) => t.id === id)
      ?.notificationType ?? ""
  );
}

/** Payload body of `POST /public/admin/notification-settings/{operatorId}`. */
interface CreateNotificationBody {
  active: number;
  triggerTypeId: number;
  notificationTypeId: number;
  address: string;
  subject: string;
}

/** Payload body of `PUT /public/admin/notification-settings/{id}`. */
type EditNotificationBody = Partial<CreateNotificationBody> & { id: number };

function listNotifications(): NotificationSetting[] {
  return notifications;
}

/**
 * Append a new row built from the create body, mapping the submitted enum ids
 * to their labels so the re-fetched list shows the values the user picked.
 */
function createNotification(
  operatorId: number,
  body: CreateNotificationBody
): void {
  const nextId =
    notifications.reduce((max, row) => Math.max(max, row.id), 200) + 1;
  const now = "2026-01-09T09:00:00.000Z";
  notifications.push({
    id: nextId,
    operatorId,
    companyId: 1,
    triggerTypeId: body.triggerTypeId,
    triggerType: triggerTypeLabel(body.triggerTypeId),
    notificationTypeId: body.notificationTypeId,
    notificationType: notificationTypeLabel(body.notificationTypeId),
    address: body.address,
    templateId: 0,
    templateName: "",
    active: body.active,
    subject: body.subject,
    createdAt: now,
    updatedAt: now,
  });
}

/** Patch a row in place with the changed fields so the re-fetch reflects them. */
function editNotification(body: EditNotificationBody): void {
  const row = notifications.find((n) => n.id === body.id);
  if (!row) return;
  if (body.triggerTypeId !== undefined) {
    row.triggerTypeId = body.triggerTypeId;
    row.triggerType = triggerTypeLabel(body.triggerTypeId);
  }
  if (body.notificationTypeId !== undefined) {
    row.notificationTypeId = body.notificationTypeId;
    row.notificationType = notificationTypeLabel(body.notificationTypeId);
  }
  if (body.address !== undefined) row.address = body.address;
  if (body.subject !== undefined) row.subject = body.subject;
  if (body.active !== undefined) row.active = body.active;
}

function deleteNotification(id: number): void {
  notifications = notifications.filter((n) => n.id !== id);
}

/**
 * Brand name (`GET/PUT /public/admin/brand-name`). Seeded with a value so the
 * display-name card shows it on load; the harness test user is company-scoped
 * (`permittedCompanyId=1`), so legacy renders the input enabled.
 */
let brandName: { displayName: string | null } = {
  displayName: "Acme Charging",
};

function getBrandName(): { displayName: string | null } {
  return brandName;
}

function setBrandName(displayName: string | null): {
  displayName: string | null;
} {
  brandName = { displayName };
  return brandName;
}

/**
 * Logo (`GET /public/admin/logo`, `POST /public/admin/logo/{operatorId}`).
 * Seeded with a current logo so the migrated logo card (shown only to an
 * operator-scoped session — the auth-scope seam selects one) can prove it
 * fetches and displays the current logo on load (approved fix 5a). The settings
 * card derives its display URL from `logo` alone (`s3Url + logo`), so a seeded
 * `logo` with an empty `s3Url` shows in the card; the router shell's own nav
 * logo query requires BOTH fields, so it still falls back to its default mark —
 * the seam stays invisible to the shell chrome.
 */
let logo: { logo: string; s3Url: string } = {
  logo: "seed-logo.png",
  s3Url: "",
};

function getLogo(): { logo: string; s3Url: string } {
  return logo;
}

function setLogo(next: { logo: string; s3Url: string }): void {
  logo = next;
}

export {
  NOTIFICATION_ENUMS_RESPONSE,
  listNotifications,
  createNotification,
  editNotification,
  deleteNotification,
  getBrandName,
  setBrandName,
  getLogo,
  setLogo,
};
export type {
  NotificationSetting,
  CreateNotificationBody,
  EditNotificationBody,
};
